import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, CircularProgress, Tooltip,
  ToggleButton, ToggleButtonGroup, Alert, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Switch, FormControlLabel,
  LinearProgress,
} from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import { httpClient } from '../services/http-client';
import {
  billsService, type BreakdownResponse, type BreakdownTxn,
} from '../services/bills-service';
import { accountsService } from '../services/accounts-service';
import type { Account } from '../types/api';
import { seriesColors } from '../styles/glass';
import { useColorMode } from '../theme';
import { InstitutionMark } from '../components/InstitutionMark';
import { baseMoney, useBaseCurrency } from '../stores/currency-store';

interface Cell {
  /** The bill behind the number, so a cell can be drilled into. */
  bill_id?: string;
  period: string;
  amount_due?: string | null;
  amount_paid?: string | null;
  paid_on?: string | null;
  due_date?: string | null;
  status: string;
  paid_from?: string;
  note?: string;
  /** matched | pending | unposted | none */
  reconciliation?: string;
  marked_paid_at?: string | null;
  statement_balance?: string | null;
  minimum_due?: string | null;
  posted_on?: string | null;
  posted_amount?: string | null;
  carries_balance?: boolean;
  /** unpaid | minimum | partial | full | over */
  coverage?: string;
  revolving?: string;
}

interface Row {
  payee_id: string; name: string; category: string; owner?: string;
  cells: Record<string, Cell>;
  total_paid: string; outstanding: string;
}

interface Month {
  period: string; label: string;
  total_due: string; total_paid: string;
  all_cleared: boolean; unpaid_count: number; overdue_count: number;
  unposted_count?: number;
  not_in_full_count?: number;
  revolving?: string;
}

interface Outflow {
  period: string; label: string; total: string;
  categories: Record<string, string>;
}

interface MatrixResponse {
  matrix: {
    months: Month[]; rows: Row[];
    totals: { paid: string; due: string; outstanding: string };
  };
  outflows: Outflow[];
}

/**
 * Drops the year from a backend month label ("Jan 2027" -> "Jan").
 *
 * Reason: the API formats every label with Go's "Jan 2006" layout (see
 * backend/internal/adapters/db/bill_matrix.go), but the whole page is already
 * scoped to one selected year, so repeating that year in twelve column headings
 * and twelve axis ticks is pure noise. The year has to come from the selected
 * year rather than a literal — the chart axis used to strip a hardcoded
 * ' 2026', which silently left the year attached in every other year.
 */
const monthLabel = (label: string, year: number) => label.replace(` ${year}`, '');

// A cross-account total, so it is reported in the household base currency.
// Components on this page call useBaseCurrency() so a change re-renders them.
const money = (n: string | number | null | undefined, digits = 0) => baseMoney(n, digits);

/**
 * The spreadsheet view: payees down, months across.
 *
 * Answers the question the owner actually asks each month — "did every card
 * get paid?" — without reading a table row by row.
 */
export const BillMatrixPage: React.FC = () => {
  // Subscribes this page to the base currency so money() relabels on change.
  useBaseCurrency();
  const [data, setData] = useState<MatrixResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'CreditCard'>('all');
  // The sheet this replaces shows every payee every month, including the ones
  // with nothing owed, so "hide empty" is opt-in rather than the default.
  const [hideEmpty, setHideEmpty] = useState(false);
  // Year gives the whole-year shape; month reproduces the sheet's own columns
  // for one period, which is what a monthly reconcile actually needs.
  const [view, setView] = useState<'year' | 'month'>('year');
  const [month, setMonth] = useState<string>(() => new Date().toISOString().slice(0, 7));
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // The cell being edited: which payee, which month.
  const [edit, setEdit] = useState<{ row: Row; period: string; cell?: Cell } | null>(null);

  // The drill-down: a number, what it is made of, and one level further into a
  // single merchant.
  const [drill, setDrill] = useState<{ row: Row; period: string; billId: string } | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownResponse | null>(null);
  const [drillBusy, setDrillBusy] = useState(false);
  const [merchant, setMerchant] = useState<string | null>(null);
  const [merchantTxns, setMerchantTxns] = useState<BreakdownTxn[]>([]);
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paidOn, setPaidOn] = useState('');
  const [paidFrom, setPaidFrom] = useState('');
  // The year was hardcoded, so there was no way to review a prior year.
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const { mode } = useColorMode();

  useEffect(() => {
    setData(null);
    httpClient
      .get<MatrixResponse>(`/bills/matrix?from=${year}-01-01&to=${year}-12-01`)
      .then(setData)
      .catch((e) => setError(e?.message ?? 'Failed to load matrix'));
  }, [year]);

  // Reason: the month selector is scoped to the loaded year, so switching year
  // with a stale month would show a blank table for a period not in range.
  useEffect(() => {
    setMonth((m) => (m.startsWith(String(year)) ? m : `${year}-01`));
  }, [year]);

  useEffect(() => {
    accountsService.getAccounts({ per_page: 200 })
      .then((r) => {
        const list = r?.data?.data;
        setAccounts(Array.isArray(list) ? list : []);
      })
      .catch(() => setAccounts([]));
  }, []);

  const palette = useMemo(() => seriesColors(mode), [mode]);

  const rows = useMemo(() => {
    const all = data?.matrix.rows ?? [];
    // Every payee is a row by default. Dropping the quiet ones would also drop
    // the ones a future month needs to be entered against, which is the whole
    // point of being able to type into the grid.
    const shown = hideEmpty
      ? all.filter((r) => Number(r.total_paid) > 0 || Number(r.outstanding) > 0)
      : all;
    return filter === 'all' ? shown : shown.filter((r) => r.category === filter);
  }, [data, filter, hideEmpty]);

  // Cash accounts AND cards: a Chexy bill is charged to a credit card.
  const fundingAccounts = useMemo(
    () => accounts.filter((a) => a.account_type === 'Asset' || a.account_type === 'Debt'),
    [accounts],
  );

  /**
   * Clicking a number asks what it is made of; clicking an empty cell goes
   * straight to entry, because there is nothing yet to explain.
   */
  const openDrill = async (row: Row, period: string) => {
    const cell = row.cells[period];
    if (!cell?.bill_id) {
      openCell(row, period);
      return;
    }
    setDrill({ row, period, billId: cell.bill_id });
    setBreakdown(null);
    setMerchant(null);
    setMerchantTxns([]);
    setDrillBusy(true);
    try {
      setBreakdown(await billsService.breakdown(cell.bill_id));
    } catch (e: any) {
      setNotice(e?.message ?? 'Could not load the breakdown');
      setDrill(null);
    } finally {
      setDrillBusy(false);
    }
  };

  /** The last level: the individual charges behind one merchant. */
  const openMerchant = async (name: string) => {
    if (!drill) return;
    if (merchant === name) {
      setMerchant(null);
      setMerchantTxns([]);
      return;
    }
    setMerchant(name);
    setMerchantTxns([]);
    try {
      const r = await billsService.breakdown(drill.billId, name);
      setMerchantTxns(r.transactions ?? []);
    } catch {
      setMerchantTxns([]);
    }
  };

  /** Open the editor for one payee-month, prefilled from whatever is there. */
  const openCell = (row: Row, period: string) => {
    const cell = row.cells[period];
    setEdit({ row, period, cell });
    setAmount(cell?.amount_due ?? '');
    setDueDate(cell?.due_date?.slice(0, 10) ?? '');
    setPaidOn(cell?.paid_on?.slice(0, 10) ?? '');
    setPaidFrom('');
    setNotice(null);
  };

  const reload = () => {
    httpClient
      .get<MatrixResponse>(`/bills/matrix?from=${year}-01-01&to=${year}-12-01`)
      .then(setData)
      .catch((e) => setNotice(e?.message ?? 'Failed to reload'));
  };

  /**
   * Save one cell.
   *
   * Two separate writes on purpose: the bill records what is owed, and the
   * payment records that it was settled. Entering an amount must never imply
   * the second, which is why "Paid on" is its own field rather than inferred
   * from the amount being present.
   */
  const saveCell = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      const saved = await billsService.saveBill({
        id: edit.cell ? undefined : undefined,
        payee_id: edit.row.payee_id,
        period: `${edit.period}-01`,
        due_date: dueDate || undefined,
        amount_due: amount || undefined,
        status: paidOn ? undefined : (amount ? 'Due' : 'Upcoming'),
      });
      if (paidOn && amount) {
        await billsService.recordPayment(saved.id, {
          amount,
          paid_on: paidOn,
          from_account_id: paidFrom || undefined,
          statement_balance: amount,
        });
      }
      setNotice(`Saved ${edit.row.name} · ${edit.period}.`);
      setEdit(null);
      reload();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const outflowChart = useMemo(() => {
    if (!data?.outflows?.length) return null;
    const cats = new Set<string>();
    data.outflows.forEach((o) => Object.keys(o.categories).forEach((c) => cats.add(c)));
    const catList = [...cats].sort();
    const max = Math.max(1, ...data.outflows.map((o) => Number(o.total)));

    // Whole-year total per category, for the pie.
    const totals = catList.map((c) => ({
      name: c,
      value: data.outflows.reduce((sum, o) => sum + Number(o.categories[c] ?? 0), 0),
    })).filter((t) => t.value > 0).sort((a, b) => b.value - a.value);
    const grand = totals.reduce((s, t) => s + t.value, 0) || 1;

    // Cumulative pie slices as SVG arc paths.
    let angle = -Math.PI / 2;
    const slices = totals.map((t) => {
      const frac = t.value / grand;
      const start = angle;
      const end = angle + frac * Math.PI * 2;
      angle = end;
      const large = end - start > Math.PI ? 1 : 0;
      const R = 78, cx = 90, cy = 90;
      const x1 = cx + R * Math.cos(start), y1 = cy + R * Math.sin(start);
      const x2 = cx + R * Math.cos(end), y2 = cy + R * Math.sin(end);
      // A single-category year would otherwise draw a degenerate zero-length arc.
      const d = frac >= 0.999
        ? `M ${cx} ${cy - R} A ${R} ${R} 0 1 1 ${cx - 0.01} ${cy - R} Z`
        : `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
      return { ...t, d, pct: (frac * 100) };
    });

    // Month-over-month total, as a polyline for the trend chart.
    const W = 640, HGT = 150, pad = 8;
    const pts = data.outflows.map((o, i) => {
      const x = pad + (i * (W - pad * 2)) / Math.max(1, data.outflows.length - 1);
      const y = HGT - pad - (Number(o.total) / max) * (HGT - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');

    return { catList, max, totals, grand, slices, pts, W, HGT };
  }, [data]);

  if (error) return <Typography color="error" sx={{ p: 3 }}>{error}</Typography>;
  if (!data) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 360, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Building the grid…</Typography>
      </Box>
    );
  }

  const { months, totals } = data.matrix;
  const notCleared = months.filter((m) => !m.all_cleared);
  // Marked paid but never posted to an account: the actionable state.
  const unposted = months.reduce((n, m) => n + (m.unposted_count ?? 0), 0);
  // Cards paid, but not cleared: the balance keeps accruing interest.
  const notInFull = months.reduce((n, m) => n + (m.not_in_full_count ?? 0), 0);
  const revolving = months.reduce((n, m) => n + Number(m.revolving ?? 0), 0);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Every card, every month</Typography>
          <Typography variant="h3">Payment grid</Typography>
          <Typography variant="body2" color="text.secondary">
            {rows.length} payees × {months.length} months · click any cell to enter it
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <Button size="small" variant="outlined" onClick={() => setYear((y) => y - 1)}>
              ← {year - 1}
            </Button>
            <Typography variant="h6" sx={{ minWidth: 64, textAlign: 'center' }}>{year}</Typography>
            <Button
              size="small" variant="outlined"
              onClick={() => setYear((y) => y + 1)}
              // Next year is reachable so bills can be entered ahead; beyond
              // that there is nothing to plan against.
              disabled={year >= new Date().getFullYear() + 1}
            >
              {year + 1} →
            </Button>
            <ToggleButtonGroup
              size="small" exclusive value={view}
              onChange={(_, v) => v && setView(v)}
              sx={{ ml: 1 }}
            >
              <ToggleButton value="year">Year</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
            </ToggleButtonGroup>
            {view === 'month' && (
              <TextField
                select size="small" value={month}
                onChange={(e) => setMonth(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                {months.map((m) => (
                  <MenuItem key={m.period} value={m.period}>{m.label}</MenuItem>
                ))}
              </TextField>
            )}
            <FormControlLabel
              sx={{ ml: 1 }}
              control={<Switch size="small" checked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)} />}
              label={<Typography variant="caption" color="text.secondary">
                Hide payees with no activity
              </Typography>}
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={3} sx={{ textAlign: 'right' }}>
          <Metric label="Paid" value={money(totals.paid)} varName="--inflow-text" />
          <Metric label="Outstanding" value={money(totals.outstanding, 2)}
            varName={Number(totals.outstanding) > 0 ? '--warn-text' : '--inflow-text'} />
        </Stack>
      </Stack>

      <Alert severity={notCleared.length === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
        {notCleared.length === 0
          ? 'Every card and bill with a balance was cleared in every month shown.'
          : `Not fully cleared: ${notCleared.map((m) => m.label).join(', ')}.`}
      </Alert>

      {notInFull > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>
            {notInFull} card statement{notInFull === 1 ? '' : 's'} not paid in full
            — {money(revolving)} still revolving.
          </strong>{' '}
          A partial or minimum payment leaves the remainder accruing interest.
          Those cells are marked below with the shortfall.
        </Alert>
      )}

      {unposted > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{unposted} bill{unposted === 1 ? '' : 's'} marked paid but never posted.</strong>{' '}
          Someone recorded a payment, but no matching transaction appeared in the
          funding account within the grace window — worth checking the bill was
          actually collected. Cells are outlined in amber below.
        </Alert>
      )}

      {view === 'month' && (() => {
        const label = months.find((m) => m.period === month)?.label ?? month;
        // The sheet's own columns, in the sheet's own order: this view exists
        // to be reconciled against it line by line.
        const monthRows = rows
          .map((r) => ({ row: r, cell: r.cells[month] }))
          .filter((x) => !hideEmpty || x.cell);
        const billed = monthRows.reduce((n, x) => n + Number(x.cell?.amount_due ?? 0), 0);
        const paid = monthRows.reduce((n, x) => n + Number(x.cell?.amount_paid ?? 0), 0);
        return (
            <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center"
                sx={{ mb: 1.5 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {monthRows.length} payees · click a row to break the number down
                  </Typography>
                </Box>
                <Stack direction="row" spacing={3} sx={{ textAlign: 'right' }}>
                  <Metric label="Billed" value={money(billed, 2)} varName="--text" />
                  <Metric label="Paid" value={money(paid, 2)} varName="--inflow-text" />
                </Stack>
              </Stack>

              <Box component="table" sx={{
                width: '100%', borderCollapse: 'collapse',
                '& th, & td': {
                  padding: '6px 10px', fontSize: 13,
                  borderBottom: '1px solid var(--border-soft)', whiteSpace: 'nowrap',
                },
                '& th': { textAlign: 'left', color: 'var(--text-dim)', fontWeight: 600 },
                '& tbody tr:hover td': { background: 'var(--glass-1)', cursor: 'pointer' },
              }}>
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th>Due Date</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'center' }}>Paid</th>
                    <th>Paid on</th>
                    <th>Paid by</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {monthRows.map(({ row: r, cell: c }) => {
                    const isPaid = c?.status === 'Paid';
                    return (
                      <tr key={r.payee_id} onClick={() => openDrill(r, month)}>
                        <td>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <InstitutionMark name={r.name} size={16} />
                            <span>{r.name}</span>
                            <Typography variant="caption" color="text.secondary">
                              {r.owner || r.category}
                            </Typography>
                          </Box>
                        </td>
                        <td>{c?.due_date ? c.due_date.slice(0, 10) : '-'}</td>
                        <td className="fw-num" style={{ textAlign: 'right' }}>
                          {c?.amount_due ? money(c.amount_due, 2) : '-'}
                        </td>
                        <td style={{
                          textAlign: 'center',
                          color: isPaid ? 'var(--inflow-text)' : 'var(--text-faint)',
                          fontWeight: isPaid ? 700 : 400,
                        }}>
                          {isPaid ? 'Y' : '-'}
                        </td>
                        <td>{c?.paid_on ? c.paid_on.slice(0, 10) : '-'}</td>
                        <td>{c?.paid_from || '-'}</td>
                        <td style={{ whiteSpace: 'normal', color: 'var(--text-dim)' }}>
                          {c?.note || '-'}
                        </td>
                      </tr>
                    );
                  })}
                  {monthRows.length === 0 && (
                    <tr><td colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        Nothing recorded for {label}.
                      </Typography>
                    </td></tr>
                  )}
                </tbody>
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {view === 'year' && (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary">Grid</Typography>
            <ToggleButtonGroup size="small" exclusive value={filter}
              onChange={(_, v) => v && setFilter(v)}>
              <ToggleButton value="all">All bills</ToggleButton>
              <ToggleButton value="CreditCard">Credit cards</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{
              borderCollapse: 'separate', borderSpacing: 0, minWidth: 900, width: '100%',
              '& th, & td': { padding: '7px 10px', whiteSpace: 'nowrap', fontSize: '0.8125rem' },
              '& thead th': {
                position: 'sticky', top: 0, zIndex: 2,
                background: 'var(--surface-solid)',
                borderBottom: '1px solid var(--border)',
                fontSize: '0.6875rem', fontWeight: 750, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: 'var(--text-faint)',
              },
              // The payee column stays visible while the months scroll.
              '& .sticky-col': {
                position: 'sticky', left: 0, zIndex: 1,
                background: 'var(--surface-solid)',
                borderRight: '1px solid var(--border)',
              },
              '& thead .sticky-col': { zIndex: 3 },
              '& tbody tr:hover td': { background: 'var(--glass-1)' },
            }}>
              <thead>
                <tr>
                  <th className="sticky-col" style={{ textAlign: 'left' }}>Payee</th>
                  {months.map((m) => (
                    <th key={m.period} style={{ textAlign: 'right' }}>
                      {monthLabel(m.label, year)}
                    </th>
                  ))}
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.payee_id}>
                    <td className="sticky-col">
                      {/* Most payees ARE institutions (Amex, MBNA, Rogers), so
                          the mark doubles as a scan target down the frozen
                          column. Small enough not to change the row height. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <InstitutionMark name={r.name} size={18} />
                        <Typography variant="body2" fontWeight={600} component="span" sx={{
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.owner || r.category}
                        </Typography>
                      </Box>
                    </td>
                    {months.map((m) => {
                      const c = r.cells[m.period];
                      const paid = Number(c?.amount_paid ?? 0);
                      const due = Number(c?.amount_due ?? 0);
                      const unpaid = c && c.status !== 'Paid' && due > 0;
                      // Only a revolving account can have a meaningful shortfall;
                      // a utility bill paid short is just a partial payment.
                      const shortfall = c?.carries_balance ? Number(c.revolving ?? 0) : 0;
                      return (
                        <td key={m.period} className="fw-num"
                          onClick={() => openDrill(r, m.period)}
                          title="Click to see what this number is made of"
                          style={{ textAlign: 'right', cursor: 'pointer' }}>
                          {!c ? (
                            <Box component="span" sx={{ color: 'var(--text-faint)' }}>·</Box>
                          ) : paid > 0 ? (
                            <Tooltip title={
                              `Paid ${money(paid, 2)}${c.paid_on ? ` on ${c.paid_on.slice(0, 10)}` : ''}` +
                              `${c.paid_from ? ` from ${c.paid_from}` : ''}` +
                              `${c.due_date ? ` · due ${c.due_date.slice(0, 10)}` : ''}` +
                              `${c.statement_balance ? ` · statement ${money(c.statement_balance, 2)}` : ''}` +
                              `${c.posted_on ? ` · posted ${c.posted_on.slice(0, 10)}` : ''}` +
                              (shortfall > 0
                                ? ` — ${c.coverage === 'minimum' ? 'MINIMUM ONLY' : 'PARTIAL'}: ${money(shortfall, 2)} still revolving`
                                : c.coverage === 'full' ? ' — paid in full' : '') +
                              (c.reconciliation === 'unposted'
                                ? ' — MARKED PAID BUT NEVER POSTED: no matching transaction appeared'
                                : c.reconciliation === 'pending'
                                  ? ' — awaiting the transaction to post'
                                  : c.reconciliation === 'matched'
                                    ? ' — matched to a posted transaction'
                                    : '')
                            }>
                              <Box component="span" sx={{
                                color: shortfall > 0
                                  ? 'var(--danger-text)'
                                  : c.reconciliation === 'unposted'
                                    ? 'var(--warn-text)' : 'var(--inflow-text)',
                                ...(c.reconciliation === 'unposted' && {
                                  border: '1px solid var(--warn-text)',
                                  borderRadius: '4px', px: 0.5,
                                }),
                                ...(shortfall > 0 && {
                                  border: '1px solid var(--danger-text)',
                                  borderRadius: '4px', px: 0.5,
                                }),
                              }}>
                                {money(paid, 0)}
                                {c.reconciliation === 'unposted' ? '?' : ''}
                                {shortfall > 0 ? '▲' : ''}
                              </Box>
                            </Tooltip>
                          ) : unpaid ? (
                            <Tooltip title={`${money(due, 2)} outstanding · ${c.status}`}>
                              <Box component="span" sx={{
                                color: c.status === 'Overdue' ? 'var(--danger-text)' : 'var(--warn-text)',
                                fontWeight: 650,
                              }}>
                                {money(due, 0)}!
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box component="span" sx={{ color: 'var(--text-faint)' }}>—</Box>
                          )}
                        </td>
                      );
                    })}
                    <td className="fw-num" style={{ textAlign: 'right', fontWeight: 650 }}>
                      {money(r.total_paid)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="sticky-col" style={{ borderTop: '1px solid var(--border)' }}>
                    <Typography variant="caption" color="text.secondary">Month total</Typography>
                  </td>
                  {months.map((m) => (
                    <td key={m.period} className="fw-num"
                      style={{ textAlign: 'right', borderTop: '1px solid var(--border)' }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        <span>{money(m.total_paid)}</span>
                        {Number(m.total_due) > 0 && (
                          m.all_cleared
                            ? <CheckCircle sx={{ fontSize: 13, color: 'var(--inflow-text)' }} />
                            : <Tooltip title={`${m.unpaid_count} unpaid`}>
                                <ErrorOutline sx={{ fontSize: 13, color: 'var(--warn-text)' }} />
                              </Tooltip>
                        )}
                      </Stack>
                    </td>
                  ))}
                  <td className="fw-num" style={{
                    textAlign: 'right', fontWeight: 750, borderTop: '1px solid var(--border)',
                  }}>
                    {money(totals.paid)}
                  </td>
                </tr>
              </tfoot>
            </Box>
          </Box>
        </CardContent>
      </Card>
      )}

      {outflowChart && (
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Money out by category</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              From the ledger — actual spending, excluding card payments (which
              would double-count the original purchase).
            </Typography>

            {/* Trend line: total out per month. The stacked bars below show the
                mix; this shows the direction, which bars alone read poorly. */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Monthly total — trend</Typography>
              <Box component="svg" viewBox={`0 0 ${outflowChart.W} ${outflowChart.HGT}`}
                sx={{ width: '100%', height: 150, display: 'block' }}>
                <polyline
                  points={outflowChart.pts} fill="none"
                  stroke="var(--outflow)" strokeWidth={2}
                  strokeLinejoin="round" strokeLinecap="round"
                />
                {data.outflows.map((o, i) => {
                  const x = 8 + (i * (outflowChart.W - 16)) / Math.max(1, data.outflows.length - 1);
                  const y = outflowChart.HGT - 8
                    - (Number(o.total) / outflowChart.max) * (outflowChart.HGT - 16);
                  return (
                    <circle key={o.period} cx={x} cy={y} r={2.5} fill="var(--outflow)">
                      <title>{`${o.label}: ${money(o.total)}`}</title>
                    </circle>
                  );
                })}
              </Box>
            </Box>

            {/* Share of spend for the whole year. */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
              alignItems="center" sx={{ mb: 2 }}>
              <Box component="svg" viewBox="0 0 180 180"
                sx={{ width: 180, height: 180, flexShrink: 0 }}>
                {outflowChart.slices.map((sl, i) => (
                  <path key={sl.name} d={sl.d} fill={palette[i % palette.length]}
                    stroke="var(--surface-solid)" strokeWidth={1}>
                    <title>{`${sl.name}: ${money(sl.value)} (${sl.pct.toFixed(1)}%)`}</title>
                  </path>
                ))}
              </Box>
              <Box sx={{ flex: 1, width: '100%' }}>
                {outflowChart.slices.slice(0, 8).map((sl, i) => (
                  <Stack key={sl.name} direction="row" alignItems="center" spacing={1}
                    sx={{ py: 0.25 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px',
                      background: palette[i % palette.length], flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ flex: 1 }}>{sl.name}</Typography>
                    <Typography variant="caption" className="fw-num"
                      color="text.secondary">{money(sl.value)}</Typography>
                    <Typography variant="caption" className="fw-num"
                      sx={{ width: 44, textAlign: 'right' }}>{sl.pct.toFixed(1)}%</Typography>
                  </Stack>
                ))}
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: 200, mb: 1 }}>
              {data.outflows.map((o) => (
                <Box key={o.period} sx={{ flex: 1, display: 'flex', flexDirection: 'column',
                  justifyContent: 'flex-end', height: '100%' }}>
                  <Tooltip title={
                    <Box>
                      <div><strong>{o.label} · {money(o.total)}</strong></div>
                      {Object.entries(o.categories)
                        .sort((a, b) => Number(b[1]) - Number(a[1]))
                        .slice(0, 8)
                        .map(([k, v]) => <div key={k}>{k}: {money(v)}</div>)}
                    </Box>
                  }>
                    <Box sx={{
                      display: 'flex', flexDirection: 'column-reverse',
                      height: `${(Number(o.total) / outflowChart.max) * 100}%`,
                      borderRadius: '4px 4px 0 0', overflow: 'hidden', cursor: 'pointer',
                      transition: 'opacity var(--dur-fast) var(--ease-out)',
                      '&:hover': { opacity: 0.82 },
                    }}>
                      {outflowChart.catList.map((cat, i) => {
                        const v = Number(o.categories[cat] ?? 0);
                        if (!v) return null;
                        return (
                          <Box key={cat} sx={{
                            height: `${(v / Number(o.total)) * 100}%`,
                            background: palette[i % palette.length],
                          }} />
                        );
                      })}
                    </Box>
                  </Tooltip>
                </Box>
              ))}
            </Stack>

            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
              {data.outflows.map((o) => (
                <Typography key={o.period} variant="caption" color="text.secondary"
                  sx={{ flex: 1, textAlign: 'center' }}>
                  {monthLabel(o.label, year)}
                </Typography>
              ))}
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {outflowChart.catList.map((cat, i) => (
                <Stack key={cat} direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%',
                    background: palette[i % palette.length] }} />
                  <Typography variant="caption" color="text.secondary">{cat}</Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {notice && (
        <Alert severity={notice.startsWith('Saved') ? 'success' : 'error'}
          onClose={() => setNotice(null)} sx={{ mt: 2 }}>
          {notice}
        </Alert>
      )}

      <Dialog open={!!drill} onClose={() => setDrill(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {drill?.row.name} · {drill?.period}
        </DialogTitle>
        <DialogContent>
          {drillBusy && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
          {breakdown && (() => {
            const b = breakdown.breakdown;
            const unexplained = Number(breakdown.unexplained);
            return (
              <>
                <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
                  <Metric label="Statement" value={money(b.amount, 2)} varName="--text" />
                  <Metric label="Charges found" value={money(b.charged, 2)}
                    varName="--text-dim" />
                  <Metric label="Transactions" value={String(b.count)} varName="--text-dim" />
                </Stack>
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mb: 1.5 }}>
                  Statement window {b.window.from.slice(0, 10)} to {b.window.to.slice(0, 10)}
                </Typography>

                {!b.explained && (
                  <Alert severity="info" sx={{ mb: 2 }}>{b.reason}</Alert>
                )}

                {/* The gap is real information, not an error to hide. */}
                {b.explained && Math.abs(unexplained) > 0.01 && (
                  <Alert severity={unexplained > 0 ? 'warning' : 'info'} sx={{ mb: 2, py: 0.5 }}>
                    <strong>{money(Math.abs(unexplained), 2)}</strong>{' '}
                    {unexplained > 0 ? 'not explained by charges' : 'more charged than billed'}
                    {' — '}interest, fees, or a balance carried from last month.
                  </Alert>
                )}

                {b.categories.map((cat) => (
                  <Box key={cat.category} sx={{ mb: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography variant="body2" fontWeight={650}>{cat.category}</Typography>
                      <Typography variant="body2" className="fw-num">
                        {money(cat.amount, 2)}
                        <Typography component="span" variant="caption"
                          color="text.secondary"> · {cat.count}</Typography>
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (Number(cat.amount) / Math.max(1, Number(b.charged))) * 100)}
                      sx={{ mb: 0.75, height: 4, borderRadius: 2 }}
                    />
                    {cat.merchants?.map((mch) => (
                      <Box key={mch.merchant}>
                        <Stack direction="row" justifyContent="space-between"
                          onClick={() => openMerchant(mch.merchant)}
                          sx={{
                            py: 0.4, pl: 1.5, cursor: 'pointer', borderRadius: 1,
                            '&:hover': { background: 'var(--glass-1)' },
                            ...(merchant === mch.merchant && { background: 'var(--glass-1)' }),
                          }}>
                          <Typography variant="caption" sx={{ color: 'var(--text-dim)' }}>
                            {merchant === mch.merchant ? '▾' : '▸'} {mch.merchant}
                            {mch.count > 1 ? ` ×${mch.count}` : ''}
                          </Typography>
                          <Typography variant="caption" className="fw-num">
                            {money(mch.amount, 2)}
                          </Typography>
                        </Stack>
                        {merchant === mch.merchant && (
                          <Box sx={{ pl: 3.5, pb: 0.75 }}>
                            {merchantTxns.length === 0 && (
                              <Typography variant="caption" color="text.secondary">
                                Loading…
                              </Typography>
                            )}
                            {merchantTxns.map((t) => (
                              <Stack key={t.id} direction="row" justifyContent="space-between"
                                sx={{ py: 0.2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {t.date.slice(0, 10)}
                                </Typography>
                                <Typography variant="caption" className="fw-num">
                                  {money(t.amount, 2)}
                                </Typography>
                              </Stack>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            const d = drill;
            setDrill(null);
            if (d) openCell(d.row, d.period);
          }}>Edit this month</Button>
          <Button variant="contained" onClick={() => setDrill(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!edit} onClose={() => !busy && setEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {edit?.row.name} · {edit?.period}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Type the month the way you would in the sheet. Leaving &quot;Paid on&quot;
            blank records what is owed without claiming it was settled.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Amount" size="small" fullWidth autoFocus value={amount}
              onChange={(e) => setAmount(e.target.value)}
              helperText="The statement balance. Blank means nothing owed this month."
            />
            <TextField
              label="Due date" type="date" size="small" fullWidth value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Paid on" type="date" size="small" fullWidth value={paidOn}
              onChange={(e) => setPaidOn(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Fill this in only once the money has actually left."
            />
            {paidOn && (
              <TextField
                label="Paid from" select size="small" fullWidth value={paidFrom}
                onChange={(e) => setPaidFrom(e.target.value)}
                helperText="Defaults to the payee's funding account"
              >
                {fundingAccounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                ))}
              </TextField>
            )}
            {paidOn && !amount && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                An amount is needed before a payment can be recorded.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={saveCell} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

const Metric: React.FC<{ label: string; value: string; varName: string }> = ({ label, value, varName }) => (
  <Box>
    <Typography variant="overline" color="text.secondary">{label}</Typography>
    <Typography className="fw-num" variant="h5" sx={{ color: `var(${varName})` }}>{value}</Typography>
  </Box>
);
